import { fetchWrapper } from 'helpers/helpers';
import type { PaginatedResponse, Uuid, UserSimple } from 'store/reducers/types';

type DashboardBody = Record<string, unknown>;

export interface DashboardOfferDecision {
  entry_date: string | null;
  probation_period: number | null;
}

export interface DashboardCandidateOpening {
  opening_id: Uuid | null;
  expected: string | null;
  level: string | null;
}

export interface DashboardRelatedInterview {
  date: string | null;
  from_time: string | null;
  to_time: string | null;
  link: string | null;
  locations: string[] | null;
  uuid: Uuid | null;
}

export interface DashboardRelatedReminder {
  type: string | null;
  datetime: string | null;
}

export interface DashboardRelatedFeedback {
  feedback_uuid: Uuid | null;
  is_draft: boolean | null;
  posted_at: string | null;
}

export interface DashboardRelatedAssignment {
  step_type: string | null;
  step_uuid: Uuid | null;
  job_name: string | null;
  job_uuid: Uuid | null;
  service: string | null;
  service_id: Uuid | null;
  status: string | null;
  offer_decision: DashboardOfferDecision | null;
  opening: DashboardCandidateOpening | null;
  team_lead_full_name: string | null;
  job_assignment_uuid: Uuid | null;
}

export interface DashboardCandidateRowBase {
  uuid: Uuid;
  level: string;
  full_name_local: string;
  full_name_latin: string;
  expected_answer_date: string | null;
  start_date: string | null;
  cvs: Uuid[];
  cvs_links: string[];
  assignment: DashboardRelatedAssignment | null;
  reminder: DashboardRelatedReminder | null;
  interview: DashboardRelatedInterview | null;
  feedback: DashboardRelatedFeedback | null;
}

export type DashboardCandidateRow = DashboardCandidateRowBase & {
  recruiter_uuid?: Uuid | null;
  recruiter_full_name?: string | null;
  is_action_required?: boolean;
};

export interface DashboardCandidatesGroupBase {
  group_name: string | null;
  candidates_count: number;
  candidates: DashboardCandidateRow[];
}

export interface DashboardCandidatesByJobsGroup extends DashboardCandidatesGroupBase {
  service_name?: string | null;
  service_id?: Uuid;
  uuid?: Uuid;
}

export type DashboardCandidatesByStepsGroup = DashboardCandidatesGroupBase;
export type DashboardCandidatesByActionsGroup = DashboardCandidatesGroupBase;

export interface DashboardOpening {
  uuid: Uuid;
  expected: string | null;
  created: string;
  status: string;
  service_uuid: Uuid | null;
  service_name: string | null;
  comment?: string | null;
  priority: string;
  job_uuid: Uuid;
  job_name: string;
  rm_approval_date?: string | null;
  lm_approved_at?: string | null;
  team_lead: UserSimple;
  level: string | null;
  custom_level: string | null;
  locations?: string[];
}

export interface DashboardTeamLeadOpening {
  uuid: Uuid;
  job_uuid: Uuid;
  job_name: string;
  status: string;
  priority: string;
  service_uuid: Uuid | null;
  service_name: string | null;
  level: string;
}

export interface DashboardServiceOpeningsRow {
  uuid: Uuid;
  name: string;
  candidates_count: number;
  openings_count: number;
  total_openings_count: number;
  openings: DashboardOpening[];
}

export interface DashboardJobOpeningsRow {
  uuid: Uuid;
  name: string;
  candidates_count: number;
  openings_count: number;
  total_openings_count: number;
  service_uuid: Uuid;
  service_name: string;
  openings: DashboardOpening[];
}

export interface DashboardJobRow {
  uuid: Uuid;
  name: string;
  service: string;
  status: string;
  updated_at: string;
  /* HR-Lead variant adds the two annotated fields below. */
  openings_count?: number;
  opening_status?: string;
}

export interface DashboardNewOpening {
  uuid: Uuid;
  status: string;
  service: string;
  service_uuid: Uuid;
  job_uuid: Uuid;
  job_name: string;
  priority: string;
}

export interface DashboardSSCVacancy {
  uuid: Uuid;
  name: string;
}

export interface DashboardSSCRow {
  uuid: Uuid;
  hubspot_id: string | null;
  full_name_latin: string;
  email: string[];
  phone: string[];
  comment: string | null;
  submitted_at: string;
  has_potential_duplicates: boolean;
  vacancy: DashboardSSCVacancy | null;
  candidate_id: Uuid;
  viewed_by_list: string[];
}

export const getSAJobsTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardJobOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobOpeningsRow>>({
    url: 'dashboard/super-admin/openings/jobs/',
    version: 'v2',
    body,
    key: 'dashboard-super-admin-openings-jobs',
  });

export const getSAServicesTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardServiceOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardServiceOpeningsRow>>({
    url: 'dashboard/super-admin/openings/services/',
    version: 'v2',
    body,
    key: 'dashboard-super-admin-openings-services',
  });

export const getHRLJobsDashboardAPI = (): Promise<PaginatedResponse<DashboardJobRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobRow>>({
    url: 'dashboard/hr-lead/jobs/',
    version: 'v2',
    key: 'dashboard-hr-lead-jobs',
  });

export const getHRLJobsTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardJobOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobOpeningsRow>>({
    url: 'dashboard/hr-lead/openings/jobs/',
    version: 'v2',
    body,
    key: 'dashboard-hr-lead-openings-jobs',
  });

export const getHRLServicesTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardServiceOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardServiceOpeningsRow>>({
    url: 'dashboard/hr-lead/openings/services/',
    version: 'v2',
    body,
    key: 'dashboard-hr-lead-openings-services',
  });

/* The candidates endpoint serializes one of:
   - `<Role>CandidatesDashboardSerializer` (no grouping)
   - `<Role>JobsForCandidatesDashboardSerializer` (`/jobs/` grouping)
   - `<Role>StepsForCandidatesDashboardSerializer` (`/steps/` grouping)
   - `<Role>ActionsForCandidatesDashboardSerializer` (`/actions/` grouping)
   Callers pick the grouping suffix; the union covers all four shapes. */
export type DashboardCandidatesResponse =
  | PaginatedResponse<DashboardCandidateRow>
  | PaginatedResponse<DashboardCandidatesByJobsGroup>
  | PaginatedResponse<DashboardCandidatesByStepsGroup>
  | PaginatedResponse<DashboardCandidatesByActionsGroup>;

export const getCandidatesTableDashboardAPI = (
  dashboard: string,
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/${dashboard}/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-hr-lead-active-candidates',
  });

export const getRecruiterLeadJobsDashboardAPI = (): Promise<PaginatedResponse<DashboardJobRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobRow>>({
    url: 'dashboard/recruiter-lead/jobs/',
    version: 'v2',
    key: 'dashboard-recruiter-lead-jobs',
  });

export const getRecruiterLeadCandidatesTableDashboardAPI = (
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/recruiter-lead/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-recruiter-lead-candidates',
  });

export const getRecruiterCandidatesTableDashboardAPI = (
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/recruiter/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-recruiter-candidates',
  });

export const getRecruiterNewOpeningsDashboardAPI = (): Promise<
  PaginatedResponse<DashboardNewOpening> | null | undefined
> =>
  fetchWrapper<PaginatedResponse<DashboardNewOpening>>({
    url: 'dashboard/recruiter/new-openings/',
    version: 'v2',
    key: 'dashboard-recruiter-new-openings',
  });

export const markRecruiterNewOpeningReadAPI = (openingUuid: Uuid): Promise<null | undefined> =>
  fetchWrapper<null>({
    url: `dashboard/recruiter/new-openings/${openingUuid}/mark-as-read/`,
    method: 'POST',
    version: 'v2',
    body: {
      opening_id: openingUuid,
    },
    key: 'dashboard-recruiter-new-openings-mark-as-read',
  });

export const getRecruiterSSCTableDashboardAPI = (
  dashboard: string,
  body: DashboardBody
): Promise<PaginatedResponse<DashboardSSCRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardSSCRow>>({
    url: `dashboard/${dashboard}/self-submitted-candidates/`,
    version: 'v2',
    body,
    key: 'dashboard-self-submitted-candidates',
  });

export const getRMServicesTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardServiceOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardServiceOpeningsRow>>({
    url: 'dashboard/resource-manager/openings/services/',
    version: 'v2',
    body,
    key: 'dashboard-resource-manager-openings-services',
  });

export const getRMJobsTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardJobOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobOpeningsRow>>({
    url: 'dashboard/resource-manager/openings/jobs/',
    version: 'v2',
    body,
    key: 'dashboard-resource-manager-openings-jobs',
  });

export const getTLActiveOpeningsDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardTeamLeadOpening> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardTeamLeadOpening>>({
    url: 'dashboard/team-lead/openings/',
    version: 'v2',
    body,
    key: 'dashboard-team-lead-openings',
  });

export const getTLCandidatesTableDashboardAPI = (
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/team-lead/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-team-lead-candidates',
  });

export const getLMOpeningsTableDashboardAPI = (
  body: DashboardBody,
  grouping: string
): Promise<
  PaginatedResponse<DashboardJobOpeningsRow> | PaginatedResponse<DashboardServiceOpeningsRow> | null | undefined
> =>
  fetchWrapper<PaginatedResponse<DashboardJobOpeningsRow> | PaginatedResponse<DashboardServiceOpeningsRow>>({
    url: `dashboard/location-manager/openings/${grouping}/`,
    version: 'v2',
    body,
    key: 'dashboard-location-manager-openings',
  });

export const getSHServicesTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardServiceOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardServiceOpeningsRow>>({
    url: 'dashboard/service-head/openings/services/',
    version: 'v2',
    body,
    key: 'dashboard-service-head-openings-services',
  });

export const getSHJobsTableDashboardAPI = (
  body: DashboardBody
): Promise<PaginatedResponse<DashboardJobOpeningsRow> | null | undefined> =>
  fetchWrapper<PaginatedResponse<DashboardJobOpeningsRow>>({
    url: 'dashboard/service-head/openings/jobs/',
    version: 'v2',
    body,
    key: 'dashboard-service-head-openings-jobs',
  });

export const getSHCandidatesTableDashboardAPI = (
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/service-head/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-service-head-candidates',
  });

export const getInterviewerCandidatesTableDashboardAPI = (
  body: DashboardBody,
  grouping = ''
): Promise<DashboardCandidatesResponse | null | undefined> =>
  fetchWrapper<DashboardCandidatesResponse>({
    url: `dashboard/interviewer/candidates/${grouping && `${grouping}/`}`,
    version: 'v2',
    body,
    key: 'dashboard-interviewer-candidates',
  });
